import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

// Supported locales
export const supportedLocales = ['en', 'el'];
export const defaultLocale = 'el';

// Reactive current locale
export const currentLocale = ref(localStorage.getItem('lang') || defaultLocale);

// Reactive locales list (for switcher)
export const locales = ref([
    { label: 'English', value: 'en' },
    { label: 'Greek / Ελληνικά', value: 'el' }
]);



export function fixLocaleInUrl(to, from, next) {

    let locale = to.params.locale;
    const savedLocale = localStorage.getItem("locale") || defaultLocale;

    if (!locale) {
        return next({ path: `/${savedLocale}${to.path}`, query: to.query });
    }

    if (!supportedLocales.includes(locale)) {
        return next({ path: `/${defaultLocale}${to.path.replace(/^\/[a-z]{2}/, "")}`, query: to.query });
    }

    if (savedLocale !== locale) {
        localStorage.setItem("locale", locale);
    }

    next();
}

// Add locale to route paths
export function withLocale(route) {
    return {
        ...route,
        path: `/:locale(${supportedLocales.join('|')})${route.path === '/' ? '' : route.path}`
    };
}


// Composable
export function useLocalization() {
    const router = useRouter();
    const route = useRoute();
    const i18n = useI18n();

    const getCurrentLocale = computed(() => {
        const localeFromRoute = route.params.locale;
        if (supportedLocales.includes(localeFromRoute)) return localeFromRoute;
        return localStorage.getItem('lang') || defaultLocale;
    });

    const updateLocale = (newLocale) => {
        if (!supportedLocales.includes(newLocale)) newLocale = defaultLocale;

        i18n.locale.value = newLocale;
        localStorage.setItem('lang', newLocale);
        currentLocale.value = newLocale;

        // update URL without reload
        const currentPath = route.fullPath.replace(/^\/[a-z]{2}/, '');
        router.replace({ path: `/${newLocale}${currentPath}`, query: route.query });
    };

    const detectAndSetLocale = () => {

        let preferredLocale = localStorage.getItem("locale") || defaultLocale;

        if (!supportedLocales.includes(preferredLocale)) {
            preferredLocale = defaultLocale;
        }

        if (route.params.locale === preferredLocale) {
            return;
        }

        let newPath = route.fullPath.replace(/^\/[a-z]{2}($|\/)/, "/");

        router.replace({ path: `/${preferredLocale}${newPath}`, query: route.query });
    };

    // Watch URL changes and sync locale
    watch(
        () => route.params.locale,
        (newLocale) => {
            if (newLocale && supportedLocales.includes(newLocale)) {
                updateLocale(newLocale);
            }
        },
        { immediate: true }
    );
    

    return {
        currentLocale,
        getCurrentLocale,
        updateLocale,
        detectAndSetLocale,
        locales
    };
}
